import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { StatusStateEnum, TopicLabelTypeEnum } from "~/types/enums";
import Modal from "~/components-shared/Modal";
import toastsActionCreators from "~/store/toasts/actions";
import { Alert, Grid, Text } from "~/components-shared";
import topicLabelSchema from "~/forms/TopicLabelForm/topicLabelSchema";
import TopicLabelForm from "~/forms/TopicLabelForm";
import topicSelectors from "~/store/topics/selectors";
import topicActionCreators from "~/store/topics/actions";
import { TopicActionEnum } from "~/store/topics/types";
const StyledGrid = styled(Grid)`
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const StickyAlert = styled(Alert)`
  position: sticky;
  top: 0;
  z-index: 2;
`;
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
`;
const Color = styled.div`
  width: 1.4rem;
  height: 1.4rem;
  background-color: ${_ref => {
  let {
    $color,
    theme
  } = _ref;
  return $color || theme.colors.borders.secondary;
}};
  border-radius: 50%;
  margin-right: 0.6rem;
  display: inline-block;
  vertical-align: sub;
`;
const TopicLabelModal = () => {
  const {
    t
  } = useTranslation();
  const topic = useSelector(topicSelectors.getSelected);
  const action = useSelector(topicSelectors.getAction);
  const status = useSelector(topicSelectors.getLabelStatus);
  const labels = useSelector(topicSelectors.getLabels);
  const error = useSelector(topicSelectors.getLabelError);
  const dispatch = useDispatch();
  const defaultValues = useMemo(() => ({
    color: "",
    title: "",
    description: "",
    currentTopic: false
  }), []);
  const {
    handleSubmit,
    control,
    reset
  } = useForm({
    resolver: yupResolver(topicLabelSchema),
    defaultValues
  });
  useEffect(() => {
    if (action === TopicActionEnum.MANAGE_LABELS) {
      reset(defaultValues);
    }
  }, [defaultValues, action, reset]);
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(topicActionCreators.fetchLabelsStarted());
    }
  }, [status]);
  const onSubmit = handleSubmit(data => {
    if (topic?.id) {
      dispatch(topicActionCreators.createLabelStarted({
        ...data,
        topicId: data.currentTopic ? topic.id : undefined
      }, _ref2 => {
        let {
          data: newLabel
        } = _ref2;
        if (newLabel) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TopicLabelModal.labelCreated", {
              name: newLabel.title
            }),
            timeout: 10000
          }));
          reset(defaultValues);
        }
      }));
    }
  });
  const handleDelete = id => {
    dispatch(topicActionCreators.deleteLabelStarted(id));
  };
  const handleClose = () => {
    dispatch(topicActionCreators.actionChanged(null, null));
  };
  return /*#__PURE__*/React.createElement(Modal.Body, {
    title: t("TopicLabelModal.manageLabels"),
    onClose: handleClose,
    fullWidth: true,
    maxWidth: "1000px",
    minHeight: "70vh"
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(StickyAlert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Grid, {
    cols: 5,
    gap: 3,
    template: "15% 15% 30% 25% auto",
    s: {
      pb: 3,
      pr: 3
    }
  }, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold"
  }, t("TopicLabelModal.color")), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold"
  }, t("TopicLabelModal.title")), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold"
  }, t("TopicLabelModal.description")), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold"
  }, t("TopicLabelModal.topic")), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold"
  })), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 5,
    gap: 3,
    template: "15% 15% 30% 25% auto",
    s: {
      pr: 3
    }
  }, labels.map(label => /*#__PURE__*/React.createElement(React.Fragment, {
    key: label.id
  }, /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(Color, {
    $color: label.color
  }), label.color), /*#__PURE__*/React.createElement(Text, null, label.title), /*#__PURE__*/React.createElement(Text, null, label.description || "-"), label.topic ? /*#__PURE__*/React.createElement(Text, null, label.topic?.name) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary"
  }, t("TopicLabelModal.any")), /*#__PURE__*/React.createElement(Button, {
    color: "dangerGrey",
    onClick: () => handleDelete(label.id),
    fontSize: 3,
    s: {
      px: 3,
      py: 1
    },
    disabled: label.type === TopicLabelTypeEnum.CORE,
    rounded: true
  }, t("common.actions.delete"))))), /*#__PURE__*/React.createElement(Grid, {
    cols: 5,
    gap: 3,
    template: "15% 15% 30% 25% auto",
    s: {
      pt: 3,
      pr: 3
    }
  }, /*#__PURE__*/React.createElement(TopicLabelForm, {
    control: control
  }), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: onSubmit,
    fontSize: 3,
    s: {
      mt: 0.5,
      px: 3,
      py: 2
    },
    loading: status === StatusStateEnum.LOADING,
    rounded: true
  }, t("common.actions.add"))))));
};
export default TopicLabelModal;